// GroceryListCard.js — Grocery list promotional card with total cost and product count
import React from "react";
import { useTranslation } from "react-i18next";
import Spinner from "../../UI/Spinner";

/**
 * @param {Object} props
 * @param {boolean} props.groceryListLoading
 * @param {Array} props.productList
 * @param {string} props.displayCost - Formatted cost string
 * @param {Function} props.onOpenGroceryList
 */
function GroceryListCard({ groceryListLoading, productList, displayCost, onOpenGroceryList }) {
  const { t } = useTranslation('mealplan');
  const isDisabled = groceryListLoading || productList.length === 0;

  const productCount = productList.reduce(
    (total, category) => total + (category.items?.length || 0),
    0
  );

  return (
    <div className="mt-8 max-w-5xl mx-auto px-6">
      <div
        className={`transition flex-1 min-w-[312px] p-5 overflow-hidden rounded-[12px] border-[0.5px] border-[var(--Color-Grays-Gray-2,#fff)] bg-[var(--Color-Grays-Gray-075,#EBEBEA)] flex flex-col
          ${isDisabled
            ? "cursor-not-allowed opacity-60"
            : "cursor-pointer md:hover:border-[var(--Color-Grays-Gray-0,#fff)]"
          }`}
        onClick={isDisabled ? undefined : onOpenGroceryList}
      >
        <h3 className="text-lg font-semibold mb-2">{t('grocery.title')}</h3>
        <p className="text-[#56554D] text-[14px] mb-4">
          {groceryListLoading
            ? t('grocery.generatingList')
            : t('grocery.priceDisclaimer')}
        </p>

        {/* Tags */}
        <div className="flex gap-2 mb-6">
          <span
            className="inline-flex items-center
              pl-2 pr-3 py-1
              text-sm font-medium
              rounded-[6px]
              border-[0.5px] border-[#F3F3F2]
              bg-[rgba(255,255,255,0.64)]
              shadow-[4px_12px_20px_0px_rgba(83,81,69,0.08)]
              backdrop-blur-lg"
          >
            <span className="mr-0.5 w-[24px]">
              {groceryListLoading ? (
                <Spinner size="sm" color="#0D0C07" />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.9342 4.57344C10.3538 4.8132 10.4996 5.3477 10.2598 5.76728L7.67453 10.2915H9.48954C9.49731 10.2914 9.50505 10.2914 9.51278 10.2915H14.4874C14.4951 10.2914 14.5029 10.2914 14.5106 10.2915H16.3256L13.7404 5.76728C13.5006 5.3477 13.6464 4.8132 14.066 4.57344C14.4855 4.33369 15.02 4.47946 15.2598 4.89904L18.3412 10.2915H19.1477C19.1598 10.2912 19.172 10.2912 19.1843 10.2915H20.3334C20.8167 10.2915 21.2084 10.6832 21.2084 11.1665C21.2084 11.6497 20.8167 12.0415 20.3334 12.0415H19.8635L18.605 17.5197C18.4843 18.0977 18.1661 18.6158 17.7048 18.9849C17.2429 19.3544 16.6667 19.5511 16.0755 19.5415H13.6795C13.6717 19.5416 13.664 19.5416 13.6562 19.5415H10.344C10.3362 19.5416 10.3284 19.5416 10.3207 19.5415H7.92462C7.33342 19.5511 6.75724 19.3544 6.29538 18.9849C5.83245 18.6145 5.51359 18.0941 5.39387 17.5135L4.21072 12.0415H3.66675C3.1835 12.0415 2.79175 11.6497 2.79175 11.1665C2.79175 10.6832 3.1835 10.2915 3.66675 10.2915H4.89978C4.91169 10.2912 4.92358 10.2912 4.93542 10.2915H5.65897L8.74037 4.89904C8.98013 4.47946 9.51463 4.33369 9.9342 4.57344ZM6.00116 12.0415L6.43359 14.0415H8.93914L8.71692 12.0415H6.18212C6.17135 12.0417 6.16055 12.0417 6.14976 12.0415H6.00116ZM10.4777 12.0415L10.6999 14.0415H13.3003L13.5225 12.0415H10.4777ZM15.2832 12.0415L15.061 14.0415H17.6085L18.0679 12.0415H17.8504C17.8396 12.0417 17.8288 12.0417 17.818 12.0415H15.2832ZM17.2065 15.7915H14.8666L14.6444 17.7915H16.0834L16.1011 17.7917C16.2863 17.7954 16.4669 17.7341 16.6116 17.6184C16.7562 17.5026 16.8557 17.3398 16.8927 17.1583L16.8973 17.1372L17.2065 15.7915ZM12.8836 17.7915L13.1058 15.7915H10.8944L11.1166 17.7915H12.8836ZM9.35581 17.7915L9.13359 15.7915H6.81197L7.10532 17.1482L7.1075 17.1583L7.10744 17.1583C7.14445 17.3398 7.24394 17.5026 7.38859 17.6184C7.53325 17.7341 7.7139 17.7954 7.89911 17.7917L7.91675 17.7915H9.35581Z"
                    fill="#0D0C07"
                  />
                </svg>
              )}
            </span>
            <span className="ml-0 font-normal">
              <span className="font-semibold">{t('grocery.total')}</span>{" "}
              {groceryListLoading ? "" : displayCost}
            </span>
          </span>
          <span
            className="inline-flex items-center
              px-2 pl-1 py-1
              text-sm font-medium
              rounded-[6px]
              border-[0.5px] border-[#F3F3F2]
              bg-[rgba(255,255,255,0.64)]
              shadow-[4px_12px_20px_0px_rgba(83,81,69,0.08)]
              backdrop-blur-lg"
          >
            <span className="text-[#FFCB00]">#</span>{" "}
            <span className="font-semibold">{productCount}</span>{" "}
            &nbsp;{t('grocery.products')}
          </span>
        </div>

        {/* Grocery illustration */}
        <div
          className="flex w-full aspect-[3/2] sm:aspect-[3/1] rounded-[6px] mb-5 items-center justify-center relative bg-cover bg-top"
          style={{
            backgroundImage: `url(/assets/grocery-promo.jpg)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          {groceryListLoading && (
            <div className="absolute inset-0 bg-black bg-opacity-30 rounded-[6px] flex items-center justify-center">
              <Spinner size="lg" color="#FFFFFF" />
            </div>
          )}
        </div>

        <button
          className={`relative
            inline-flex justify-center items-center gap-1
            h-[44px] px-[24px] py-[14px]
            rounded-[8px]
            border-[1px] 
            text-[#0D0C07] text-center
            text-[14px] font-medium leading-[28px]
            transition-colors
            w-full
            bg-[#0D0C07]
            shadow-[0_2px_16px_0_rgba(255,212,41,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_1px_rgba(255,213,50,1)]
            cursor-pointer bg-[linear-gradient(200deg,_#FFCB00%,_#FFD532_100%)] md:hover:border-[0.5px] md:hover:border-[#FFCB00] md:hover:bg-[#FFCB00]
                ${isDisabled
                  ? "border-[#E2E1DF] bg-[#E2E1DF] cursor-not-allowed shadow-[inset,_0_0px_0px_1px_rgba(226,225,223,1)]"
                  : "border-[#EBEBEA] bg-[#FFCB00] md:hover:border-[#FFD429] md:hover:bg-[#FFD429]"
                }
              `}
          onClick={(e) => {
            e.stopPropagation();
            if (!isDisabled) onOpenGroceryList();
          }}
          disabled={isDisabled}
        >
          {groceryListLoading ? (
            <>
              <Spinner size="sm" color="#0D0C07" />
              <span>{t('grocery.generatingList', 'Putting your grocery list together...')}</span>
            </>
          ) : (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="25"
                viewBox="0 0 24 25"
                fill="none"
              >
                <path
                  d="M14.0662 4.88495C14.4595 4.66033 14.9539 4.77419 15.2117 5.13495L15.2595 5.21015L18.3416 10.6027H20.3337C20.8167 10.6029 21.2086 10.9947 21.2087 11.4777C21.2087 11.9609 20.8168 12.3526 20.3337 12.3527H19.863L18.6033 17.841L18.6013 17.84C18.4792 18.4143 18.1636 18.929 17.7048 19.2961C17.2451 19.6639 16.6721 19.8595 16.0837 19.8518V19.8527H7.91675V19.8518C7.32831 19.8595 6.75547 19.6639 6.29565 19.2961C5.83125 18.9246 5.51116 18.4023 5.39233 17.8195V17.8185L4.21069 12.3527H3.66675C3.1835 12.3527 2.79175 11.961 2.79175 11.4777C2.79192 10.9946 3.18361 10.6027 3.66675 10.6027H5.65894L8.73999 5.21015L8.78882 5.13495C9.04667 4.77426 9.54105 4.66022 9.93433 4.88495C10.3277 5.10977 10.4806 5.59389 10.3005 5.99921L10.2595 6.07929L7.67456 10.6027H16.3259L13.74 6.07929L13.7 5.99921C13.5198 5.59384 13.6727 5.10978 14.0662 4.88495ZM14.6453 18.1027H16.0837L16.1013 18.1037L16.239 18.0939C16.3745 18.0728 16.5028 18.0165 16.6111 17.9299C16.7557 17.8142 16.8553 17.6514 16.8923 17.4699L16.8972 17.4484L17.2068 16.1027H14.8669L14.6453 18.1027ZM7.10522 17.4601L7.10718 17.4699L7.14624 17.6018C7.19658 17.7297 7.27988 17.843 7.38843 17.9299C7.53308 18.0456 7.71396 18.1074 7.89917 18.1037C7.90503 18.1036 7.91089 18.1027 7.91675 18.1027H9.35522L9.13354 16.1027H6.81226L7.10522 17.4601ZM11.116 18.1027H12.8835L13.1052 16.1027H10.8943L11.116 18.1027ZM15.0613 14.3527H17.6082L18.0681 12.3527H15.283L15.0613 14.3527ZM10.7 14.3527H13.3005L13.5222 12.3527H10.4783L10.7 14.3527ZM6.43335 14.3527H8.93921L8.71753 12.3527H6.00171L6.43335 14.3527Z"
                  fill="#0D0C07"
                />
              </svg>
              <span>{t('actions.viewGroceriesList')}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default GroceryListCard;
